/**
 * Turning a pasted spreadsheet block into series data.
 *
 * The team already works in columns: they select a block in the sheet and paste it into
 * Slack today. Asking them to fill 300 numeric inputs instead would be slower than the
 * process this replaces, so the editor takes the same paste and parses it.
 *
 * Accepts tab, comma or run-of-spaces separated columns, with or without a header row,
 * and normalises the period labels the sheet actually uses:
 *
 *   2021-Q1   Q1 2021   2021 Q1        -> 2021-Q1
 *   2021      2026 YTD  2026 (YTD)     -> 2021 / 2026
 *   1/1/2022  Jan 2022  2022-01        -> 2022-01
 *
 * Rows whose value cells are all blank are dropped, which is what makes a partially
 * filled new quarter work: the sheet carries a 2026-Q3 row with empty cells for weeks
 * before the number arrives, and pasting it should not create a gap in the chart.
 */

export interface ParsedRow {
  period: string;
  values: number[];
}

export interface ParseResult {
  rows: ParsedRow[];
  errors: string[];
  skipped: number;
}

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};

export type SeriesKind = 'quarterly' | 'annual' | 'monthly';

/** Returns a normalised period key, or null when the cell is not a period at all. */
export function normalisePeriod(raw: string, kind: SeriesKind): string | null {
  const s = String(raw ?? '').trim().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return null;

  if (kind === 'quarterly') {
    let m = /^(\d{4})[-\s]*Q([1-4])$/i.exec(s);
    if (m) return `${m[1]}-Q${m[2]}`;
    m = /^Q([1-4])[-\s]*(\d{4})$/i.exec(s);
    if (m) return `${m[2]}-Q${m[1]}`;
    return null;
  }

  if (kind === 'annual') {
    // "2026 YTD" and "2026 (YTD)" both mean the year 2026. The partial marker is
    // derived from the as-of date, so it is deliberately discarded here.
    const m = /^(\d{4})(?:\s*(?:YTD|QTD|TD))?$/i.exec(s);
    return m ? m[1] : null;
  }

  // monthly
  let m = /^(\d{4})[-/](0?[1-9]|1[0-2])$/.exec(s);
  if (m) return `${m[1]}-${String(Number(m[2])).padStart(2, '0')}`;

  m = /^([A-Za-z]{3,9})[-\s]+(\d{4})$/.exec(s);
  if (m) {
    const mm = MONTHS[m[1].toLowerCase()];
    return mm ? `${m[2]}-${String(mm).padStart(2, '0')}` : null;
  }

  // The pricing block in the workbook uses M/D/YYYY, always the first of the month.
  m = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(\d{4})$/.exec(s);
  if (m) return `${m[3]}-${String(Number(m[1])).padStart(2, '0')}`;

  return null;
}

function splitCells(line: string): string[] {
  if (line.includes('\t')) return line.split('\t');
  if (line.includes(',')) return line.split(',');
  return line.split(/\s{2,}| (?=[$\d(-])/);
}

function toNumber(raw: string): number | null {
  const s = String(raw ?? '').trim().replace(/[$,%\s]/g, '');
  if (!s || s === '-' || s === '--' || /^n\/?a$/i.test(s)) return null;
  // (1.23) is an accounting negative.
  const negated = /^\((.*)\)$/.exec(s);
  const body = negated ? `-${negated[1]}` : s;
  const n = Number(body);
  return Number.isFinite(n) ? n : null;
}

export function parsePastedSeries(
  text: string,
  kind: SeriesKind,
  valueColumns: number,
): ParseResult {
  const errors: string[] = [];
  const rows: ParsedRow[] = [];
  let skipped = 0;

  const lines = String(text ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  for (const line of lines) {
    const cells = splitCells(line).map((c) => c.trim());
    if (!cells.length) continue;

    const period = normalisePeriod(cells[0], kind);
    if (!period) {
      // A header row, or the block title from the sheet. Skipping quietly is correct:
      // pasting "Retirement Quarterly<TAB>Index Value 3 Aug 2026" should just work.
      skipped += 1;
      continue;
    }

    const values = cells.slice(1).map(toNumber).filter((v): v is number => v !== null);
    if (values.length === 0) {
      // A period that exists in the sheet but has no data yet. Dropping it is what lets
      // a new quarter appear in the source weeks before its number does.
      skipped += 1;
      continue;
    }
    if (values.length < valueColumns) {
      errors.push(
        `${period}: found ${values.length} number${values.length === 1 ? '' : 's'} but ` +
          `${valueColumns} are needed. Check the columns you copied.`,
      );
      continue;
    }
    rows.push({ period, values: values.slice(0, valueColumns) });
  }

  // Only when nothing at all parsed AND no row explained itself, so a "found 2 numbers
  // but 3 are needed" message is not followed by a vaguer one contradicting it.
  if (!rows.length && lines.length && !errors.length) {
    errors.push('Nothing in that paste looked like a period and a value. Include the label column.');
  }

  // Duplicates are usually a double paste, and would otherwise surface later as a
  // confusing "the next period must be" gap error.
  const seen = new Set<string>();
  for (const r of rows) {
    if (seen.has(r.period)) errors.push(`${r.period} appears more than once in that paste.`);
    seen.add(r.period);
  }

  rows.sort((a, b) => (a.period < b.period ? -1 : a.period > b.period ? 1 : 0));
  return { rows, errors, skipped };
}

/** Renders series data back into the same tab-separated form, for round-tripping. */
export function seriesToText(periods: string[], columns: number[][]): string {
  return periods
    .map((p, i) => [p, ...columns.map((c) => c[i])].join('\t'))
    .join('\n');
}

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * A period key as a person would say it, for button and summary labels.
 *
 * Deliberately without any "(QTD)" marker: whether a period counts as still open is
 * decided by the as-of date on the server, and a label here that guessed at it could
 * contradict what the page ends up showing.
 */
export function humanPeriod(period: string): string {
  const q = /^(\d{4})-Q([1-4])$/.exec(period);
  if (q) return `Q${q[2]} ${q[1]}`;
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (m) return `${MONTH_SHORT[Number(m[2]) - 1]} ${m[1]}`;
  return period;
}
